/**
 * Storage Layer - Punto de entrada principal (WEB)
 *
 * Este archivo exporta las interfaces y el factory unificado.
 * Los adapters Dexie se registran automáticamente para web.
 *
 * USO:
 * import { getRepositories, animalStorage } from './services/storage';
 * const { animal, ubicacion } = getRepositories();
 */

// ==================== Auto-registro para Web ====================
// Importar al inicio para cumplir con ESLint, pero ejecutar al final
import { registerDexieAdapters } from './adapters/dexie';

// ==================== Interfaces ====================
export type {
  IRepository,
  IBulkRepository,
  ISoftDeleteRepository,
  IAnimalRepository,
  IUbicacionRepository,
  IUnidadRepository,
  IEmpresaRepository,
  IOperacionCompraVentaRepository,
  IProfileRepository,
  IDatabase,
  DatabaseConfig,
  CurrentUserInfo,
  AnimalFilters,
  AnimalStatistics,
  AnimalPaginatedResult,
  Animal,
  AnimalHistorico,
  Ubicacion,
  Unidad,
  Empresa,
  OperacionCompraVentaAnimales,
  Profiles,
  // Interfaces de administración
  IGastoRepository,
  IIngresoRepository,
  IStockRepository,
  IStockHistoricoRepository,
  ICategoriaCacheRepository,
  // Taxonomía de conceptos
  IConceptoRepository,
  ICategoriaRepository,
  ConceptoOculto,
  ConceptoConfirmado,
  Categoria,
  Concepto,
  // Tipos de administración
  Gasto,
  Ingreso,
  Stock,
  StockHistorico,
  CategoriaCache,
  TipoOperacionStock,
  TipoCategoriaCache,
  FiltroGastos,
  FiltroIngresos,
  FiltroStocks,
  FiltroStockHistorico,
  StockPaginatedResult,
  StockStatistics,
  StockHistoricoPaginatedResult,
  StockHistoricoStatistics,
  // Interfaces de notas y herramientas
  INotaRepository,
  Nota,
  NotaFilters,
  IEventoRepository,
  FiltroEventos,
  IRecordatorioRepository,
  FiltroRecordatorios,
  ILluviaRepository,
  Lluvia,
  FiltroLluvias,
  IElementoMapaRepository,
  ElementoMapa,
  ElementoMapaTipo,
  GeometriaMapa,
  FiltroElementosMapa,
  IPuntoControlRepository,
  IPuntoControlEventoRepository,
  PuntoControl,
  PuntoControlEvento,
  FotoEventoControl,
  CondicionPastura,
  DensidadPastura,
  CalculoPasturaSnapshot,
  CorteMs,
  ISiembraLoteRepository,
  SiembraLote,
  CicloSiembra,
  INotaAnclaRepository,
  NotaAncla,
  TipoAncla,
  IPersonaCampoRepository,
  PersonaCampo,
  TipoNota,
  Evento,
  Recordatorio,
  // Interfaces de historial
  IAnimalHistoricoRepository,
  FiltroAnimalHistorico,
  AnimalHistoricoPaginatedResult
} from './interfaces';

// ==================== Factory Unificado ====================
export {
  DatabaseFactory,
  getDatabaseFactory,
  getCurrentUserInfo,
  getRepositories,
  isFactoryReady
} from './DatabaseFactory';

export type { Platform } from './DatabaseFactory';
export type { DatabaseAdapters } from './DatabaseFactory';

// ==================== Dexie Adapters ====================
export {
  registerDexieAdapters,
  isDexieAdaptersRegistered,
  createSyncAdapter,
  DexieDatabase,
  getDexieDatabase,
  DexieAdapter
} from './adapters/dexie';

// ==================== Nueva Arquitectura Unificada ====================
// Database Adapters (abstraen operaciones específicas de BD)
// IDatabaseAdapter es un detalle de implementación interna, no se exporta
export type {
  WhereClause,
  OrderByClause,
  TransactionOperation,
  WhereOperator
} from './adapters/IDatabaseAdapter';

// Repositorios unificados (compartidos web + mobile)
export {
  BaseRepository,
  ProfileRepository,
  GastoRepository,
  IngresoRepository,
  UbicacionRepository,
  UnidadRepository,
  EmpresaRepository,
  NotaRepository,
  EventoRepository,
  RecordatorioRepository,
  LluviaRepository,
  CategoriaCacheRepository,
  StockRepository,
  StockHistoricoRepository,
  AnimalHistoricoRepository,
  OperacionCompraVentaRepository,
  AnimalRepository,
  ConceptoRepository,
  CategoriaRepository
} from './repositories';

// ==================== Sync Helpers (shared with mobile) ====================
export {
  emitInsert,
  emitUpdate,
  emitDelete,
  emitSoftDelete,
  emitRestore,
  SYNCABLE_TABLES
} from './syncHelpers';

// Constantes de puntos de control (valores, no tipos)
export {
  CONDICIONES_PASTURA,
  DENSIDADES_PASTURA,
  ETIQUETA_CONDICION,
  ETIQUETA_DENSIDAD
} from './interfaces/IPuntoControlRepository';

// ==================== Repository Change Events ====================
export {
  repositoryEvents
} from './RepositoryChangeEventEmitter';

export type { RepositoryChangeEvent, ChangeAction } from './RepositoryChangeEventEmitter';

// ==================== Auto-registro para Web ====================
// Registrar adapters Dexie automáticamente al importar este módulo
// Solo registrar si estamos en navegador web real (NO React Native)
//
// Detección de web vs React Native:
// - React Native tiene `window` pero no `document` real
// - React Native no tiene `navigator.product === 'ReactNative'` en producción
// - Usamos múltiples checks para estar seguros
const isWeb = () => {
  return typeof window !== 'undefined' &&
         typeof window.document !== 'undefined' &&
         document.createElement !== undefined &&
         typeof navigator !== 'undefined' &&
         navigator.product !== 'ReactNative';
};

if (isWeb()) {
  registerDexieAdapters();
}
